import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowRight, Phone } from "lucide-react";
import { Button } from "./ui/button";

// Alluz Logo Component
const AlluzLogo = ({ className = "", size = 60 }) => (
  <img 
    src="/logo-alluz.png" 
    alt="Alluz Energia" 
    className={`logo-glow ${className}`}
    style={{ width: size, height: size, objectFit: 'contain' }}
  />
);

// Photon Particle Component
const Photon = ({ delay, startX, startY }) => {
  const tx = Math.random() * 400 - 200;
  const ty = Math.random() * 400 + 200;
  
  return (
    <motion.div
      className="photon"
      style={{
        left: startX,
        top: startY,
        '--tx': `${tx}px`,
        '--ty': `${ty}px`,
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: [0, 1, 1, 0],
        scale: [0, 1, 1, 0.5],
        x: [0, tx * 0.3, tx * 0.7, tx],
        y: [0, ty * 0.3, ty * 0.7, ty],
      }}
      transition={{
        duration: 8,
        delay: delay,
        repeat: Infinity,
        ease: "linear"
      }}
    />
  );
};

// Golden Outline Solar Panel Component (no fill, just lines)
const GoldenSolarPanel = ({ style, delay, scrollYProgress }) => {
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 5]);
  
  return (
    <motion.div
      className="absolute"
      style={{ ...style, y, rotate }}
      initial={{ opacity: 0, y: 100, rotateX: 45 }}
      animate={{ opacity: 1, y: 0, rotateX: 0 }}
      transition={{ 
        duration: 1.2, 
        delay: delay,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      <svg 
        viewBox="0 0 200 140" 
        className="w-full h-full"
        style={{ filter: 'drop-shadow(0 0 15px rgba(255, 180, 20, 0.4))' }}
      >
        <defs>
          <linearGradient id={`panelGradient-${delay}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFB414" />
            <stop offset="50%" stopColor="#FF8C00" />
            <stop offset="100%" stopColor="#FFC940" />
          </linearGradient>
        </defs>
        {/* Outer frame */}
        <rect 
          x="5" y="5" 
          width="190" height="130" 
          rx="8" 
          fill="none" 
          stroke={`url(#panelGradient-${delay})`} 
          strokeWidth="3"
        />
        {/* Horizontal lines */}
        <line x1="5" y1="35" x2="195" y2="35" stroke={`url(#panelGradient-${delay})`} strokeWidth="1.5" opacity="0.7" />
        <line x1="5" y1="70" x2="195" y2="70" stroke={`url(#panelGradient-${delay})`} strokeWidth="1.5" opacity="0.7" />
        <line x1="5" y1="105" x2="195" y2="105" stroke={`url(#panelGradient-${delay})`} strokeWidth="1.5" opacity="0.7" />
        {/* Vertical lines */}
        <line x1="50" y1="5" x2="50" y2="135" stroke={`url(#panelGradient-${delay})`} strokeWidth="1.5" opacity="0.7" />
        <line x1="100" y1="5" x2="100" y2="135" stroke={`url(#panelGradient-${delay})`} strokeWidth="1.5" opacity="0.7" />
        <line x1="150" y1="5" x2="150" y2="135" stroke={`url(#panelGradient-${delay})`} strokeWidth="1.5" opacity="0.7" />
      </svg>
    </motion.div>
  );
};

export default function HeroSection() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const [photons, setPhotons] = useState([]);
  
  // Generate photons
  useEffect(() => {
    const newPhotons = [];
    for (let i = 0; i < 20; i++) {
      newPhotons.push({
        id: i,
        delay: i * 0.5,
        startX: `${10 + Math.random() * 80}%`,
        startY: `${Math.random() * 30}%`,
      });
    }
    setPhotons(newPhotons);
  }, []);
  
  // Parallax transforms
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const subtitleY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const logoScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.2]);
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <section 
      ref={heroRef}
      className="solar-bg min-h-screen relative overflow-hidden flex items-center justify-center"
      data-testid="hero-section"
    >
      {/* Sun Rays */}
      <div className="sun-rays" />
      
      {/* Panel Grid Effect */}
      <div className="panel-grid" />
      
      {/* Golden Outline Solar Panels - Hidden on mobile */}
      <div className="hidden lg:block">
        <GoldenSolarPanel 
          scrollYProgress={scrollYProgress}
          style={{ width: 180, height: 126, top: '12%', left: '3%', transform: 'rotate(-15deg)' }}
          delay={0.2}
        />
        <GoldenSolarPanel 
          scrollYProgress={scrollYProgress}
          style={{ width: 200, height: 140, top: '20%', right: '5%', transform: 'rotate(10deg)' }}
          delay={0.4}
        />
        <GoldenSolarPanel 
          scrollYProgress={scrollYProgress}
          style={{ width: 150, height: 105, bottom: '25%', left: '8%', transform: 'rotate(-8deg)' }}
          delay={0.6}
        />
        <GoldenSolarPanel 
          scrollYProgress={scrollYProgress}
          style={{ width: 170, height: 119, bottom: '18%', right: '3%', transform: 'rotate(12deg)' }}
          delay={0.8}
        />
      </div>
      
      {/* Photon Particles */}
      {photons.map((photon) => (
        <Photon 
          key={photon.id}
          delay={photon.delay}
          startX={photon.startX}
          startY={photon.startY}
        />
      ))}
      
      {/* Main Content */}
      <motion.div 
        className="container mx-auto px-6 relative z-10 text-center pt-24"
        style={{ opacity: bgOpacity }}
      >
        {/* Giant Floating Logo */}
        <motion.div
          className="mb-6"
          style={{ scale: logoScale }}
          initial={{ opacity: 0, y: -50, rotateY: 180 }}
          animate={{ opacity: 1, y: 0, rotateY: 0 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          <AlluzLogo size={160} className="mx-auto floating" />
        </motion.div>
        
        {/* Headline */}
        <motion.div style={{ y: titleY }}>
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black font-['Outfit'] leading-none mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            data-testid="hero-title"
          >
            <span className="text-[#92400E]">A Luz do </span>
            <span className="gradient-text">Futuro</span>
            <br />
            <span className="text-[#92400E]">É </span>
            <span className="gradient-text">Solar</span>
          </motion.h1>
        </motion.div>
        
        <motion.div style={{ y: subtitleY }}>
          <motion.p
            className="text-base sm:text-lg md:text-xl lg:text-2xl text-[#92400E]/70 max-w-3xl mx-auto mb-10 font-medium px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8 }}
            data-testid="hero-subtitle"
          >
            Tecnologia de ponta e engenharia financeira para{" "}
            <span className="text-[#FF8C00] font-bold">reduzir sua conta em até 95%</span>.
            <br className="hidden sm:block" />
            Transforme luz em economia. Transforme economia em liberdade.
          </motion.p>
        </motion.div>
        
        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 px-4"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
        >
          {/* Primary Button - Simular Sistema */}
          <motion.a
            href="https://simulador.alluzenergia.com.br"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="w-full sm:w-auto"
          >
            <Button
              className="btn-glow w-full sm:w-auto text-white font-bold text-base sm:text-lg md:text-xl px-8 sm:px-12 py-6 sm:py-7 rounded-2xl flex items-center justify-center gap-3 border-0"
              data-testid="hero-simular-btn"
            >
              <AlluzLogo size={28} className="!filter-none !animate-none" />
              Simular Sistema
              <ArrowRight className="w-5 h-5" />
            </Button>
          </motion.a>

          {/* Secondary Button - Fale Conosco */}
          <motion.a
            href="https://wa.me/5544988574869"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="w-full sm:w-auto"
          >
            <Button
              className="btn-outline-glow w-full sm:w-auto bg-white/80 backdrop-blur-sm text-[#92400E] font-bold text-base sm:text-lg md:text-xl px-8 sm:px-12 py-6 sm:py-7 rounded-2xl flex items-center justify-center gap-3"
              data-testid="hero-contato-btn"
            >
              <MessageCircle className="w-5 h-5 text-[#FFB414]" />
              Fale Conosco
              <Phone className="w-5 h-5 text-[#FF8C00]" />
            </Button>
          </motion.a>
        </motion.div>
        
        {/* Stats Row */}
        <motion.div
          className="mt-16 sm:mt-20 grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto px-4"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4 }}
        >
          {[
            { value: "Até 95%", label: "de Economia" },
            { value: "25+", label: "Anos de Garantia", mobileHidden: true },
            { value: "300+", label: "Projetos Entregues" },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              className={`text-center p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-[#FFB414]/20 to-[#FF8C00]/10 border border-[#FFB414]/30 backdrop-blur-sm ${stat.mobileHidden ? 'hidden sm:block' : ''}`}
              whileHover={{ 
                scale: 1.05, 
                backgroundColor: 'rgba(255, 180, 20, 0.25)',
                borderColor: 'rgba(255, 180, 20, 0.6)'
              }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5 + index * 0.1 }}
            >
              <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black font-['Outfit'] gradient-text stat-glow mb-1">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-[#92400E]/70 font-medium">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
      
      {/* Energy Flow Lines */}
      <div className="energy-line hidden sm:block" style={{ width: '40%', top: '30%', left: '-10%', transform: 'rotate(30deg)' }} />
      <div className="energy-line hidden sm:block" style={{ width: '30%', bottom: '25%', right: '-5%', transform: 'rotate(-20deg)', animationDelay: '1s' }} />
    </section>
  );
}
